const STRINGS = [
  { id: "E2", label: "E", octave: 2, frequency: 82.41 },
  { id: "A2", label: "A", octave: 2, frequency: 110.0 },
  { id: "D3", label: "D", octave: 3, frequency: 146.83 },
  { id: "G3", label: "G", octave: 3, frequency: 196.0 },
  { id: "B3", label: "B", octave: 3, frequency: 246.94 },
  { id: "E4", label: "E", octave: 4, frequency: 329.63 },
] as const;

const frequencyEl = document.getElementById("frequency") as HTMLDivElement;
const statusEl = document.getElementById("status") as HTMLParagraphElement;
const targetEl = document.getElementById("target-string") as HTMLParagraphElement;
const offsetEl = document.getElementById("offset") as HTMLParagraphElement;
const indicatorEl = document.getElementById("indicator") as HTMLDivElement;
const levelFill = document.getElementById("level-fill") as HTMLDivElement;
const levelText = document.getElementById("level-text") as HTMLDivElement;
const noiseSlider = document.getElementById("noise-slider") as HTMLInputElement;
const noiseValue = document.getElementById("noise-value") as HTMLSpanElement;
const toggleButton = document.getElementById("toggle") as HTMLButtonElement;
const stringButtonsContainer = document.getElementById(
  "string-buttons"
) as HTMLDivElement;
const stringsContainer = document.getElementById("strings") as HTMLDivElement;

type GuitarString = (typeof STRINGS)[number];

type AudioState = {
  context: AudioContext | null;
  analyser: AnalyserNode | null;
  data: Float32Array | null;
  raf: number | null;
  stream: MediaStream | null;
  running: boolean;
  smoothedFrequency: number | null;
  wakeLock: WakeLockSentinel | null;
  lastLevel: number;
  noiseGate: number;
  indicatorPercent: number;
};

let selectedStringId: GuitarString["id"] | null = null;
const stringOffsets = new Map<string, HTMLDivElement>();
const stringRows = new Map<string, HTMLDivElement>();

const audioState: AudioState = {
  context: null,
  analyser: null,
  data: null,
  raf: null,
  stream: null,
  running: false,
  smoothedFrequency: null,
  wakeLock: null,
  lastLevel: 0,
  noiseGate: 0.01,
  indicatorPercent: 0.5,
};
const sliderToGate = (value: number) => value / 1200;
const formatGate = (gate: number) => `${Math.round(gate * 1200)}% input`;

function formatFrequency(value: number | null): string {
  if (!value || Number.isNaN(value)) return "—";
  return `${value.toFixed(2)} Hz`;
}

function formatCents(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "0 cents";
  const rounded = Math.round(value * 10) / 10;
  return `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)} cents`;
}

function centsOff(current: number, target: number): number {
  return 1200 * Math.log2(current / target);
}

function findTargetString(frequency: number): GuitarString {
  if (selectedStringId) {
    const locked = STRINGS.find((s) => s.id === selectedStringId);
    if (locked) return locked;
  }

  return STRINGS.reduce((closest, candidate) => {
    const diff = Math.abs(Math.log2(frequency / candidate.frequency));
    const closestDiff = Math.abs(Math.log2(frequency / closest.frequency));
    return diff < closestDiff ? candidate : closest;
  }, STRINGS[0]);
}

function setIndicator(cents: number | null) {
  const clamped = Math.max(-50, Math.min(50, cents ?? 0));
  const target = (clamped + 50) / 100;
  // Ease the indicator to slow down movement for easier tuning.
  const eased = audioState.indicatorPercent * 0.8 + target * 0.2;
  audioState.indicatorPercent = eased;
  indicatorEl.style.left = `${eased * 100}%`;
}

function setLevel(level: number) {
  const clamped = Math.max(0, Math.min(1, level));
  const eased = audioState.lastLevel * 0.6 + clamped * 0.4;
  audioState.lastLevel = eased;
  const pct = Math.round(eased * 100);
  levelFill.style.width = `${pct}%`;
  levelText.textContent = `${pct}%`;
}

function updateStringsUI(active: GuitarString | null, cents: number | null) {
  stringRows.forEach((row, id) => {
    const isActive = active?.id === id;
    row.dataset.active = String(isActive);
    const offset = stringOffsets.get(id);
    if (!offset) return;

    if (isActive && cents !== null) {
      offset.textContent = `${cents > 0 ? "+" : ""}${cents.toFixed(1)}¢`;
      offset.dataset.tone = cents > 2 ? "sharp" : cents < -2 ? "flat" : "ok";
    } else {
      offset.textContent = "—";
      offset.dataset.tone = "";
    }
  });
}

function renderStringControls() {
  const autoChip = document.createElement("button");
  autoChip.className = "chip";
  autoChip.textContent = "Auto";
  autoChip.dataset.active = String(!selectedStringId);
  autoChip.addEventListener("click", () => {
    selectedStringId = null;
    syncChipStates();
  });
  stringButtonsContainer.appendChild(autoChip);

  STRINGS.forEach((string) => {
    const chip = document.createElement("button");
    chip.className = "chip";
    chip.textContent = `${string.label}${string.octave}`;
    chip.dataset.active = String(string.id === selectedStringId);
    chip.addEventListener("click", () => {
      selectedStringId = string.id === selectedStringId ? null : string.id;
      syncChipStates();
    });
    stringButtonsContainer.appendChild(chip);
  });
}

function syncChipStates() {
  const chips = stringButtonsContainer.querySelectorAll<HTMLButtonElement>(
    ".chip"
  );
  chips.forEach((chip) => {
    const value = chip.textContent || "";
    const matches = value === "Auto"
      ? selectedStringId === null
      : value === selectedStringId;
    chip.dataset.active = String(matches);
  });
}

function renderStringRows() {
  STRINGS.forEach((string) => {
    const row = document.createElement("div");
    row.className = "string-row";
    row.dataset.id = string.id;

    const name = document.createElement("div");
    name.className = "string-name";
    name.innerHTML = `<span>${string.label}</span><span>${string.octave}</span>`;

    const meta = document.createElement("div");
    meta.className = "string-meta";
    meta.textContent = `${string.frequency.toFixed(2)} Hz`;

    const offset = document.createElement("div");
    offset.className = "offset";
    offset.textContent = "—";

    row.append(name, meta, offset);
    stringsContainer.appendChild(row);

    stringOffsets.set(string.id, offset);
    stringRows.set(string.id, row);
  });
}

function updateStatus(text: string, tone: "neutral" | "warn" | "ok" = "neutral") {
  statusEl.textContent = text;
  statusEl.style.color = tone === "warn" ? "var(--danger)" : tone === "ok" ? "var(--accent)" : "var(--accent-3)";
}

function stopTuner() {
  if (audioState.raf) cancelAnimationFrame(audioState.raf);
  if (audioState.stream) {
    audioState.stream.getTracks().forEach((track) => track.stop());
  }
  if (audioState.context) {
    audioState.context.close().catch(() => undefined);
  }
  if (audioState.wakeLock) {
    audioState.wakeLock.release().catch(() => undefined);
    audioState.wakeLock = null;
  }

  audioState.context = null;
  audioState.analyser = null;
  audioState.data = null;
  audioState.stream = null;
  audioState.running = false;
  audioState.smoothedFrequency = null;
  toggleButton.textContent = "Start listening";
  updateStatus("Paused", "neutral");
  setIndicator(null);
  updateStringsUI(null, null);
}

async function requestWakeLock() {
  if (!("wakeLock" in navigator) || audioState.wakeLock) return;
  try {
    const lock = await (navigator as any).wakeLock.request("screen");
    audioState.wakeLock = lock;
  } catch {
    // ignore if unavailable
  }
}

async function startTuner() {
  if (audioState.running) {
    stopTuner();
    return;
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });

    const context = new AudioContext();
    await context.resume();

    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;
    analyser.smoothingTimeConstant = 0.85;

    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);

    audioState.context = context;
    audioState.analyser = analyser;
    audioState.data = new Float32Array(analyser.fftSize);
    audioState.stream = stream;
    audioState.running = true;

    toggleButton.textContent = "Stop listening";
    updateStatus("Listening…", "ok");
    requestWakeLock();
    tick();
  } catch (error) {
    console.error(error);
    updateStatus("Microphone blocked. Enable access to tune.", "warn");
  }
}

function parabolicInterpolation(correlation: Float32Array, index: number): number {
  if (index <= 0 || index >= correlation.length - 1) return 0;
  const x1 = correlation[index - 1];
  const x2 = correlation[index];
  const x3 = correlation[index + 1];
  return (x3 - x1) / (2 * (2 * x2 - x1 - x3));
}

function autoCorrelate(buffer: Float32Array, sampleRate: number, noiseGate: number): number | null {
  const size = buffer.length;
  let rms = 0;
  for (let i = 0; i < size; i++) {
    const val = buffer[i];
    rms += val * val;
  }
  rms = Math.sqrt(rms / size);
  // Gate out background noise; bump this up to reduce sensitivity.
  if (rms < noiseGate) return null;

  // Remove DC offset and apply a light Hann window to reduce leakage.
  let mean = 0;
  for (let i = 0; i < size; i++) mean += buffer[i];
  mean /= size;
  for (let i = 0; i < size; i++) {
    const multiplier = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (size - 1)));
    buffer[i] = (buffer[i] - mean) * multiplier;
  }

  const c = new Float32Array(size);
  for (let lag = 0; lag < size; lag++) {
    let sum = 0;
    for (let i = 0; i < size - lag; i++) {
      sum += buffer[i] * buffer[i + lag];
    }
    c[lag] = sum;
  }

  let d = 0;
  while (d < size - 1 && c[d] > c[d + 1]) d++;

  let maxIndex = -1;
  let maxValue = -Infinity;
  for (let i = d; i < size; i++) {
    if (c[i] > maxValue) {
      maxValue = c[i];
      maxIndex = i;
    }
  }

  if (maxIndex <= 0) return null;
  const shift = parabolicInterpolation(c, maxIndex);
  const period = maxIndex + shift;
  if (!Number.isFinite(period) || period === 0) return null;
  return sampleRate / period;
}

function tick() {
  if (!audioState.analyser || !audioState.data || !audioState.context) return;

  const bufferView = audioState.data as unknown as Float32Array<ArrayBuffer>;
  audioState.analyser.getFloatTimeDomainData(bufferView);

  // Show input level (RMS) to confirm the mic is flowing.
  let rms = 0;
  for (let i = 0; i < bufferView.length; i++) rms += bufferView[i] * bufferView[i];
  rms = Math.sqrt(rms / bufferView.length);
  setLevel(Math.min(rms * 12, 1));
  const gateTriggered = rms < audioState.noiseGate;
  if (gateTriggered) {
    updateStatus(`Noise gate holding input (< ${formatGate(audioState.noiseGate)})`, "warn");
    frequencyEl.textContent = formatFrequency(null);
    targetEl.textContent = selectedStringId ?? "Auto";
    offsetEl.textContent = formatCents(null);
    setIndicator(null);
    updateStringsUI(null, null);
    audioState.raf = requestAnimationFrame(tick);
    return;
  }

  const rawFrequency = autoCorrelate(audioState.data, audioState.context.sampleRate, audioState.noiseGate);

  if (rawFrequency) {
    const smoothed = audioState.smoothedFrequency
      ? audioState.smoothedFrequency * 0.8 + rawFrequency * 0.2
      : rawFrequency;
    audioState.smoothedFrequency = smoothed;

    const target = findTargetString(smoothed);
    const cents = centsOff(smoothed, target.frequency);

    frequencyEl.textContent = formatFrequency(smoothed);
    targetEl.textContent = `${target.label}${target.octave}`;
    offsetEl.textContent = formatCents(cents);
    setIndicator(cents);
    updateStringsUI(target, cents);
    updateStatus("On mic", "ok");
  } else {
    frequencyEl.textContent = "—";
    targetEl.textContent = selectedStringId ?? "Auto";
    offsetEl.textContent = "0 cents";
    setIndicator(null);
    updateStringsUI(null, null);
    updateStatus("Listening for a clean note…");
  }

  audioState.raf = requestAnimationFrame(tick);
}

function bootstrap() {
  renderStringControls();
  renderStringRows();
  toggleButton.addEventListener("click", startTuner);
  const initialGate = sliderToGate(Number(noiseSlider.value));
  audioState.noiseGate = initialGate;
  noiseValue.textContent = formatGate(initialGate);
  noiseSlider.addEventListener("input", () => {
    const gate = sliderToGate(Number(noiseSlider.value));
    audioState.noiseGate = gate;
    noiseValue.textContent = formatGate(gate);
  });
}

bootstrap();
